'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl,
  type SingleColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

// Type for tree specific control values
export interface TreeControlValues extends ControlValues {
  [ControlIds.Category]: string;
  [ControlIds.Value]: string;
  [ControlIds.Title]: string;
  [ControlIds.Orientation]: string;
  [ControlIds.ShowLabels]: string;
}

// Define the controls for the tree chart
export const treeControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Category,
          type: ControlType.SingleColumnSelect,
          label: 'Category path column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Value,
          type: ControlType.SingleColumnSelect,
          label: 'Value column',
          required: true,
          numericOnly: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.Orientation,
          type: ControlType.Select,
          label: 'Orientation',
          options: [
            { label: 'Horizontal', value: 'horizontal' },
            { label: 'Vertical', value: 'vertical' }
          ],
          defaultValue: 'horizontal'
        } satisfies SelectControl,
        {
          id: ControlIds.ShowLabels,
          type: ControlType.Select,
          label: 'Show labels',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl
      ]
    }
  ]
};

// Helper function to build hierarchical data from flat data
function buildHierarchicalData(
  paths: string[],
  values: number[]
): { name: string; value?: number; children?: any[] }[] {
  const root: { [key: string]: any } = {};

  paths.forEach((path, index) => {
    const parts = path.split('/').map(p => p.trim());
    let current = root;

    parts.forEach((part, i) => {
      if (!current[part]) {
        current[part] = { name: part };
        if (i === parts.length - 1) {
          current[part].value = values[index];
        } else {
          current[part].children = {};
        }
      }
      current = current[part].children || current[part];
    });
  });

  // Convert the object structure to array structure
  function convertToArray(obj: { [key: string]: any }): any[] {
    return Object.values(obj).map(node => ({
      name: node.name,
      value: node.value,
      children: node.children ? convertToArray(node.children) : undefined
    }));
  }

  return convertToArray(root);
}

// Pure function to generate ECharts config for a tree chart
export function getTreeEChartsConfig({
  data,
  title,
  orientation,
  showLabels
}: {
  data: any[];
  title?: string;
  orientation: 'horizontal' | 'vertical';
  showLabels: boolean;
}): EChartsOption {
  return {
    title: {
      text: title || 'Tree Diagram',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params.value || 0;
        return `${params.name}: ${value.toFixed(2)}`;
      }
    },
    series: [
      {
        type: 'tree',
        data: data,
        orient: orientation,
        layout: 'orthogonal',
        symbolSize: 7,
        initialTreeDepth: -1,
        label: {
          show: showLabels,
          position: orientation === 'horizontal' ? 'left' : 'top',
          rotate: orientation === 'horizontal' ? 0 : undefined,
          align: orientation === 'horizontal' ? 'right' : 'center',
          fontSize: 12,
          offset: [5, 0]
        },
        leaves: {
          label: {
            position: orientation === 'horizontal' ? 'right' : 'bottom'
          }
        },
        emphasis: {
          focus: 'descendant'
        },
        expandAndCollapse: true,
        animationDuration: 550,
        animationDurationUpdate: 750
      }
    ]
  };
}

export const treeConfig: CalculatorConfig<CalculatorId.TreeChart> = {
  id: CalculatorId.TreeChart,
  type: CalculatorType.Graph,
  name: 'Tree Diagram',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'category', 
        name: 'Category', 
        values: [
          'Company/Engineering/Frontend',
          'Company/Engineering/Backend',
          'Company/Engineering/DevOps',
          'Company/Marketing/Digital',
          'Company/Marketing/Events',
          'Company/Sales/Enterprise',
          'Company/Sales/SMB',
          'Company/HR/Recruiting'
        ] 
      },
      { 
        id: 'employees', 
        name: 'Employees', 
        values: ['15', '12', '8', '6', '4', '10', '8', '5'] 
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Category]: 'category',
    [ControlIds.Value]: 'employees',
    [ControlIds.Title]: 'Organization Structure',
    [ControlIds.Orientation]: 'horizontal',
    [ControlIds.ShowLabels]: 'true'
  },
  controls: treeControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );
    const nonNumericColumns = data.columns.filter(col => 
      !col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.Category]: nonNumericColumns[0]?.id || data.columns[0]?.id,
      [ControlIds.Value]: numericColumns[0]?.id,
      [ControlIds.Title]: 'Tree Diagram',
      [ControlIds.Orientation]: 'horizontal',
      [ControlIds.ShowLabels]: 'true'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const treeControls = controls as TreeControlValues;
    
    if (!data?.columns?.length) {
      return null;
    }

    const categoryColumn = data.columns.find(col => col.id === treeControls[ControlIds.Category]);
    const valueColumn = data.columns.find(col => col.id === treeControls[ControlIds.Value]);

    if (!categoryColumn || !valueColumn) {
      return null;
    }

    const hierarchicalData = buildHierarchicalData(
      categoryColumn.values,
      valueColumn.values.map(v => Number(v))
    );

    return getTreeEChartsConfig({
      data: hierarchicalData,
      title: treeControls[ControlIds.Title],
      orientation: treeControls[ControlIds.Orientation] as 'horizontal' | 'vertical',
      showLabels: treeControls[ControlIds.ShowLabels] === 'true'
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 